import * as Highcharts from 'highcharts';
import 'highcharts/modules/exporting';
import 'highcharts/modules/export-data';
import 'highcharts/modules/offline-exporting';
import 'highcharts/modules/boost';
import 'highcharts/modules/histogram-bellcurve';
import '../../highcharts-options';

import * as utils from '../../../utils';
import chartsStyle from '../../../../public/config/charts-style.json'

import { Aeroval } from '../../../types/global';
import { TSData, TSWeeklyData } from '../../../types/data';
declare var aeroval: Aeroval;

export function plotHistogramMultiModel(data: TSData | TSWeeklyData): void {
	plotHistogram(data as TSData, 'histogram');
}

function plotHistogram(data: TSData, id: string) {

	const models = utils.getModels();

	const tsChart = utils.getChartById('intercomp-ts');
	if (!tsChart) return;

	// set time boundaries: from zoom state or from time series data
	const [xMin, xMax] = getXBoundariesFromTimeSeries();
	if (!xMin || !xMax) return;

	// window
	var frequencies = utils.getOrderedFrequencies();
	frequencies = utils.filterFrequencies(frequencies, aeroval.frequency)
	const whichFrequencies = ['primary', 'secondary'];
	const minN = Math.min(frequencies.length, whichFrequencies.length);
	var xAxisUnit = '';

	// Precompute months in season before the main loop since it doesn't change
	const monthsInSeason = utils.monthsInSeason(aeroval.season);

	var series = [] as any[];
	for (let m: number = 0; m < models.length; m++) {
		const model = models[m]
		const modelData = data[model];
		if (!modelData) continue;

		const modelStyle = aeroval.modelStyle[model];
		const highchartsColor = utils.highchartsPalette(m);
		const colorModel = modelStyle?.color ? modelStyle.color : highchartsColor;
		const visible = typeof aeroval.seriesVisibility[model] != 'undefined' ? aeroval.seriesVisibility[model] : true;

		// Main loop optimization
		for (let i = 0; i < minN; i++) {
			const freq = frequencies[i];
			const whichFreq = whichFrequencies[i] as 'primary' | 'secondary';

			// Data arrays
			const yObs = [];
			const yMod = [];

			const keyDate = `${freq}_date` as keyof TSData[keyof TSData];
			const keyObs = `${freq}_obs` as keyof TSData[keyof TSData];
			const keyMod = `${freq}_mod` as keyof TSData[keyof TSData];

			const dateArray = modelData[keyDate] as number[];
			const obsArray = modelData[keyObs] as number[];
			const modArray = modelData[keyMod] as number[];

			if (!dateArray || !obsArray || !modArray) continue;

			// Loop over dateArray once to minimize overhead
			for (let j = 0; j < dateArray.length; j++) {
				const dateValue = dateArray[j];

				// Check date range
				if (dateValue > xMin && dateValue <= xMax) {
					const obsValue = obsArray[j];
					const modValue = modArray[j];
					const currentDate = new Date(dateValue); // Create Date object once
					const iMonth = currentDate.getMonth() + 1;
					let iYear = currentDate.getFullYear();

					if (aeroval.season === 'DJF' && iMonth === 12) {
						iYear++;
					}

					if (aeroval.season === 'All' || monthsInSeason.includes(iMonth)) {
						// If single year is selected, match year
						if (!aeroval.time.includes('-') && iYear !== parseInt(aeroval.time)) {
							continue;
						}

						// Push data in bulk
						yObs.push(obsValue);
						yMod.push(modValue);
					}
				}
			}

			const colorObs = aeroval.settings.theme === 'dark' ? chartsStyle['intercomp-ts'][`obs-${whichFreq}`].colorDarkMode : chartsStyle['intercomp-ts'][`obs-${whichFreq}`].color;
			const linkedTo = (whichFreq === 'primary') ? undefined : `${model}-primary`;
			const showInLegend = (whichFreq === 'primary');

			// model data
			series.push({
				type: 'histogram',
				name: `${model}`,
				id: `${model}-${whichFreq}`,
				color: colorModel,
				xAxis: 0,
				yAxis: 0,
				baseSeries: `m${m}${i}`,
				opacity: i == 0 ? 0.75 : 0.5,
				linkedTo,
				visible,
				showInLegend
			}, {
				id: `m${m}${i}`,
				data: yMod.filter(v => v != null),
				visible: false,
				showInLegend: false
			})

			if (m == 0) {
				// obs data
				series.push({
					type: 'histogram',
					name: freq,
					id: `Observation (${freq})`,
					color: colorObs,
					xAxis: 0,
					yAxis: 0,
					baseSeries: `o${i}`,
					opacity: i == 0 ? 0.75 : 0.5,
					showInLegend: false
				}, {
					id: `o${i}`,
					data: yObs.filter(v => v != null),
					visible: false,
					showInLegend: false
				})
			}

		}

		// get units
		var obsUnit = modelData.obs_unit;
		if (obsUnit != '1') {
			xAxisUnit = `(${obsUnit})`;
		} else {
			xAxisUnit = '';
		}
	}

	const firstModel = Object.keys(data)[0];
	const mainModel = aeroval.model.dir && aeroval.model.dir != 'dummy' ? aeroval.model.dir : firstModel;
	const mainModelVar = aeroval.menu[aeroval.parameter.dir].obs[aeroval.observation][aeroval.layer][mainModel].model_var;

	//get precomputed statistics for selected station
	var stats = {} as any;
	//if the site is a region, then take if from reg_stat data
	const keyTime = utils.getKeyTime(aeroval.time, aeroval.season);
	if (aeroval.region) {
		if (!aeroval.data.reg_stat) return;
		//get statistics
		var statistics = aeroval.data.reg_stat[mainModel][mainModelVar];

		if (statistics[aeroval.region][keyTime]['nmb'] != null) {
			var nmb = statistics[aeroval.region][keyTime]['nmb'];
			stats['nmb'] = 100 * (nmb as number);
		} else {
			stats['nmb'] = null;
		}
		if (statistics[aeroval.region][keyTime]['R'] != null) {
			var r = statistics[aeroval.region][keyTime]['R'];
			stats['R'] = r;
		} else {
			stats['R'] = null;
		}
		stats['n'] = statistics[aeroval.region][keyTime]['num_valid'];
	}

	//set title
	const titleText = getTitle(data);

	Highcharts.chart({
		chart: {
			renderTo: id,
			zooming: {
				type: 'x',
			},
		},
		boost: {
			useGPUTranslations: true,
			usePreallocated: true,
		},
		title: {
			text: titleText,
		},
		subtitle: {
			text: `${aeroval.observation} - ${aeroval.model.name}`,
		},
		tooltip: {
			shared: true,
			pointFormat: '({point.x:.2f}, {point.y:.2f})',
		},
		legend: {
			enabled: true,
			layout: 'horizontal',
			align: 'left',
			verticalAlign: 'top',
			floating: true,
			y: 50,
			x: 50,
		},
		plotOptions: {
			series: {
				events: {
					legendItemClick: function () {
						utils.clickOnLegendItem(this);
					},
				},
				label: {
					enabled: false
				}
			},
		},
		xAxis: {
			title: {
				text: `${aeroval.parameter.name} ${xAxisUnit}`,
			}
		},
		series: series,
	});
}

function getTitle(data: TSData): string {
	const firstModel = Object.keys(data)[0];
	const mainModel = aeroval.model.dir && aeroval.model.dir != 'dummy' ? aeroval.model.dir : firstModel;
	const mainModelData = data[mainModel];

	var title = aeroval.parameter.name;
	title += (aeroval.layer != 'Surface' && aeroval.layer != 'Column') ? ` (${aeroval.layer})` : ''
	const stationName = mainModelData.station_display_name ? mainModelData.station_display_name : mainModelData.station_name
	// add station and time
	const regionOrStation = aeroval.station ? stationName : aeroval.region
	title += ` - ${regionOrStation}`
	// add time
	title += ` - ${aeroval.time}`;
	// add season
	title += aeroval.season != 'All' ? ` (${aeroval.season})` : ''
	return title;
}

function getXBoundariesFromTimeSeries(): [number | undefined, number | undefined] {
	const tsChart = utils.getChartById('intercomp-ts');
	var xMin: number | undefined = undefined;
	var xMax: number | undefined = undefined;

	if (tsChart) {
		xMin = (tsChart.axes[0] as any).min;
		if (aeroval.zoom?.min) {
			xMin = aeroval.zoom.min;
		}
		xMax = (tsChart.axes[0] as any).max;
		if (aeroval.zoom?.max) {
			xMax = aeroval.zoom.max;
		}
		if (aeroval.zoom && aeroval.zoom.state === false) {
			xMin = (tsChart.axes[0] as any).dataMin;
			xMax = (tsChart.axes[0] as any).dataMax;
		}
	}

	return [xMin, xMax];
}
